/**
 * `/me/access` client (EAA-1756).
 *
 * NOTE — contract-shape gap (EAA-1714, unresolved): design-frontend.md's
 * `contracts.md` sketches a `{ app, usable, memberships }` shape. This client
 * is built against the ACTUAL portal-api response — the standard envelope
 * `{success, code, data}` wrapping the pinned `MeAccessSerializer` shape
 * `{ user, tenants: [{tenant_id, slug, name, roles, permissions}],
 * active_tenant_id }` — which is what EAA-1759 ships. Callers unwrap `.data`.
 */

export interface MeAccessUser {
    id: string;
    email: string;
    name: string;
}

export interface MeAccessTenant {
    tenant_id: string;
    slug: string;
    name: string;
    roles: string[];
    permissions: string[];
}

export interface MeAccessData {
    user: MeAccessUser;
    tenants: MeAccessTenant[];
    active_tenant_id: string | null;
}

interface ApiEnvelope<T> {
    success: boolean;
    code: number;
    message?: string;
    description?: string;
    request_id?: string;
    data: T;
}

/** Thrown on a 401 so callers can trigger `login()` instead of showing an error. */
export class MeAccessUnauthorizedError extends Error {
    constructor() {
        super('Unauthorized');
        this.name = 'MeAccessUnauthorizedError';
    }
}

export async function fetchMeAccess(
    apiBase: string,
    token: string,
    tenantId?: string | null
): Promise<MeAccessData> {
    const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
    };
    if (tenantId) {
        headers['X-Tenant-Id'] = tenantId;
    }

    const res = await fetch(`${apiBase}/api/v1/me/access`, { headers });

    if (res.status === 401) {
        throw new MeAccessUnauthorizedError();
    }
    if (!res.ok) {
        throw new Error(`GET /me/access failed with status ${res.status}`);
    }

    const envelope = (await res.json()) as ApiEnvelope<MeAccessData>;
    return envelope.data;
}
